import { useCallback, useMemo } from "react";
import { useUserScopedSettings } from "@/hooks/userSettings/useUserScopedSettings";
import {
  HOME_LAYOUT_DEFAULTS,
  HOME_LAYOUT_SCOPE,
  HomeFolder,
  HomeLayoutSettings,
  buildDefaultFolders,
  keepHiddenPages,
  moveFavoriteAmongVisible,
  moveItem,
  movePageToFolder,
  reconcileFavorites,
  reconcileFolders,
  removeFolder,
} from "@/models/HomeLayoutSettings";

export interface HomeLayoutState {
  folders: HomeFolder[];
  favoriteUrls: string[];
  /** Whether the reader's own arrangement is known yet. */
  isFetched: boolean;

  toggleFavorite: (pageUrl: string) => void;
  moveFavorite: (pageUrl: string, direction: "up" | "down") => void;

  createFolder: (name: string) => void;
  renameFolder: (folderId: string, name: string) => void;
  deleteFolder: (folderId: string) => void;
  moveFolder: (folderId: string, direction: "up" | "down") => void;
  movePage: (pageUrl: string, targetFolderId: string) => void;
}

/**
 * The reader's own home page: which folders there are, what is in them, and what is starred.
 *
 * Per reader rather than shared. Two people who use different parts of the fleet want different
 * things to hand, and a folder somebody else rearranged under them is worse than no folders.
 *
 * Nothing is stored until something is changed. Until then the arrangement is worked out from the
 * pages that exist, which is also what keeps a new page from being unreachable: it appears rather
 * than waiting for somebody to file it.
 *
 * @param knownPageUrls - The pages this reader may open, in registry order. Must be stable.
 */
export function useHomeLayout(knownPageUrls: readonly string[]): HomeLayoutState {
  const { settings, setSettings, isFetched } = useUserScopedSettings<HomeLayoutSettings>(
    HOME_LAYOUT_SCOPE,
    HOME_LAYOUT_DEFAULTS,
  );

  // Array.isArray rather than a length check: the settings store keeps a stored value whose
  // type matches the default, and `typeof null` is "object" just as an array's is. A record
  // written by an older version, or half-written, would take the whole home page down.
  const storedFolders = useMemo(
    () => (Array.isArray(settings.folders) ? settings.folders : []),
    [settings.folders],
  );
  const storedFavorites = useMemo(
    () => (Array.isArray(settings.favoriteUrls) ? settings.favoriteUrls : []),
    [settings.favoriteUrls],
  );

  const folders = useMemo(
    () =>
      storedFolders.length > 0
        ? reconcileFolders(storedFolders, knownPageUrls)
        : buildDefaultFolders(knownPageUrls),
    [storedFolders, knownPageUrls],
  );

  const favoriteUrls = useMemo(
    () => reconcileFavorites(storedFavorites, knownPageUrls),
    [storedFavorites, knownPageUrls],
  );

  /**
   * Stores an arrangement.
   *
   * The folders are always written out in full, even when the change was to one of them: what is
   * stored is the arrangement, and storing a change would need the blueprint to still be around
   * to apply it to.
   *
   * Pages the reader cannot currently open are put back first. The edit was made against what is
   * drawn, which leaves those out, so writing it as it stands would file them away for good.
   */
  const storeFolders = useCallback(
    (next: HomeFolder[]) =>
      setSettings(current => ({
        ...current,
        folders: keepHiddenPages(
          next,
          Array.isArray(current.folders) ? current.folders : [],
          knownPageUrls,
        ),
      })),
    [setSettings, knownPageUrls],
  );

  const toggleFavorite = useCallback(
    (pageUrl: string) =>
      setSettings(current => {
        const stored = Array.isArray(current.favoriteUrls) ? current.favoriteUrls : [];
        return {
          ...current,
          favoriteUrls: stored.includes(pageUrl)
            ? stored.filter(url => url !== pageUrl)
            : [...stored, pageUrl],
        };
      }),
    [setSettings],
  );

  const moveFavorite = useCallback(
    (pageUrl: string, direction: "up" | "down") =>
      setSettings(current => ({
        ...current,
        favoriteUrls: moveFavoriteAmongVisible(
          Array.isArray(current.favoriteUrls) ? current.favoriteUrls : [],
          knownPageUrls,
          pageUrl,
          direction,
        ),
      })),
    [setSettings, knownPageUrls],
  );

  const createFolder = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        return;
      }
      // Time-based rather than counted: a counted id would collide with one already stored after
      // a folder in between had been deleted.
      storeFolders([...folders, { id: `folder-${Date.now()}`, name: trimmed, pageUrls: [] }]);
    },
    [folders, storeFolders],
  );

  const renameFolder = useCallback(
    (folderId: string, name: string) => {
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        return;
      }
      storeFolders(
        folders.map(folder => (folder.id === folderId ? { ...folder, name: trimmed } : folder)),
      );
    },
    [folders, storeFolders],
  );

  const deleteFolder = useCallback(
    (folderId: string) => storeFolders(removeFolder(folders, folderId)),
    [folders, storeFolders],
  );

  const moveFolder = useCallback(
    (folderId: string, direction: "up" | "down") =>
      storeFolders(moveItem(folders, folders.findIndex(folder => folder.id === folderId), direction)),
    [folders, storeFolders],
  );

  const movePage = useCallback(
    (pageUrl: string, targetFolderId: string) =>
      storeFolders(movePageToFolder(folders, pageUrl, targetFolderId)),
    [folders, storeFolders],
  );

  return {
    folders,
    favoriteUrls,
    isFetched,
    toggleFavorite,
    moveFavorite,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    movePage,
  };
}
